// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { HardwareValues } from "@/features/stores/store-hardware-tab";
import { cn } from "@/lib/utils";

const wifiNetworkSchema = z.object({
  authMode: z.number(),
  ssid: z.string(),
  password: z.string(),
  username: z.string().nullish(),
});

// The connect timeout belongs to the primary slot only — fallbacks carry just the network credentials.
export const wifiSettingsSchema = wifiNetworkSchema.extend({ timeout: z.number().nullish() });

// A fallback with no SSID can never be joined, so unlike the primary it must name a network.
export const wifiFallbackSchema = wifiNetworkSchema.extend({ ssid: z.string().trim().min(1, "Required") });

export type WifiSettingsValues = z.infer<typeof wifiSettingsSchema>;
export type WifiFallbackValues = z.infer<typeof wifiFallbackSchema>;

export const EMPTY_WIFI_SETTINGS: WifiSettingsValues = { authMode: 0, ssid: "", password: "" };
// Every field the card renders gets a concrete value: a key left `undefined` matches the (absent)
// default, so react-hook-form would never mark it dirty even though an empty input is showing.
export const EMPTY_WIFI_FALLBACK: WifiFallbackValues = { authMode: 0, ssid: "", password: "", username: "" };

// The primary network and every fallback share their credential fields, so the same field set renders at
// either path. Fallbacks are an ordered list the device walks through when the primary can't be joined.
const PRIMARY_PATH = "fieldSettings.wifiSettings";
export type WifiSettingsPath = typeof PRIMARY_PATH | `fieldSettings.wifiFallbacks.${number}`;

export interface WifiAuthOption {
  label: string;
  value: string;
}

export function isOpenAuthLabel(label: string): boolean {
  return label.toUpperCase().includes("OPEN");
}

export function isEnterpriseAuthLabel(label: string): boolean {
  return /eap|enterprise/i.test(label);
}

const DIRTY_HIGHLIGHT = "bg-sidebar-accent-hover transition-colors";

interface WifiSettingsFieldsProps {
  path: WifiSettingsPath;
  authOptions: WifiAuthOption[];
}

export function WifiSettingsFields({ path, authOptions }: WifiSettingsFieldsProps) {
  const { t } = useTranslation("back-office");
  const { control, getFieldState } = useFormContext<HardwareValues>();
  const formState = useFormState({ control, name: path });
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);

  function dirtyClass(field: keyof WifiFallbackValues | "timeout") {
    const name = field === "timeout" ? `${PRIMARY_PATH}.timeout` : (`${path}.${field}` as const);
    return getFieldState(name, formState).isDirty && DIRTY_HIGHLIGHT;
  }

  const authMode = useWatch({ control, name: `${path}.authMode` });
  const authLabel = authOptions.find((o) => o.value === String(authMode))?.label ?? "";
  const usernameFieldVisible = isEnterpriseAuthLabel(authLabel);
  const passwordFieldVisible = !isOpenAuthLabel(authLabel);

  return (
    <div className="grid grid-cols-2 gap-4">
      <FormField
        control={control}
        name={`${path}.ssid`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("stores.hardware.fields.ssid")}</FormLabel>
            <FormControl>
              <Input {...field} className={cn(dirtyClass("ssid"))} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${path}.authMode`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("stores.hardware.fields.authMode")}</FormLabel>
            <FormControl>
              <VirtualSelectPopover
                value={String(field.value)}
                onChange={(v) => field.onChange(Number(v))}
                options={authOptions}
                placeholder={t("stores.hardware.selectAuthMode")}
                searchPlaceholder={t("stores.operations.searchOption")}
                emptyText={t("stores.operations.noOption")}
                className={cn(dirtyClass("authMode"))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {usernameFieldVisible && (
        <FormField
          control={control}
          name={`${path}.username`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("stores.hardware.fields.username")}</FormLabel>
              <FormControl>
                <Input {...field} value={field.value ?? ""} className={cn(dirtyClass("username"))} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {passwordFieldVisible && (
        <FormField
          control={control}
          name={`${path}.password`}
          render={({ field }) => (
            <FormItem className={cn(!usernameFieldVisible && "col-span-2")}>
              <FormLabel>{t("stores.hardware.fields.password")}</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={isPasswordRevealed ? "text" : "password"}
                    {...field}
                    className={cn("pr-9", dirtyClass("password"))}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => setIsPasswordRevealed((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {isPasswordRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {path === PRIMARY_PATH && (
        <FormField
          control={control}
          name={`${PRIMARY_PATH}.timeout`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("stores.hardware.fields.timeout")}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  className={cn(dirtyClass("timeout"))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
